import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that handles inline quick-add to cart functionality.
 * Clicking a size button directly adds that variant to the cart.
 *
 * @extends {Component}
 */
class QuickAddInlineComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles button click — adds the variant to the cart.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  handleClick = async (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-variant-id]'));
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const allButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (this.querySelectorAll('[data-variant-id]'));
    allButtons.forEach((btn) => (btn.disabled = true));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sectionIds.join(','),
        }),
        signal: this.#abortController.signal,
      });

      const addData = await addResponse.json();

      if (addData.status) {
        console.error('[quick-add-inline] Cart add error:', addData.message);
        return;
      }

      const cartResponse = await fetch('/cart.js', { signal: this.#abortController.signal });
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'quick-add-inline-component',
          productId: this.dataset.productId,
          variantId,
          sections: addData.sections,
        }),
      );
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') {
        console.error('[quick-add-inline] Failed to add to cart:', error);
      }
    } finally {
      allButtons.forEach((btn) => {
        if (!btn.dataset.unavailable) btn.disabled = false;
      });
    }
  };
}

if (!customElements.get('quick-add-inline-component')) {
  customElements.define('quick-add-inline-component', QuickAddInlineComponent);
}
